import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle, Wrench, ClipboardCheck, Copy } from 'lucide-react';
import { authApi } from '@/services/api.js';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';

/**
 * Password-reset confirmation page — takes a 6-digit code + new password.
 * Reads `identifier` and (in non-prod) `devOtp` from router state.
 * Direct navigation without state bounces back to /forgot-password.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const identifier = location.state?.identifier || '';
  const initialDev = location.state?.devOtp     || null;

  // NOTE: every hook below must run on EVERY render. The redirect guard for
  // the no-identifier case used to sit here, above these hooks, so the
  // component rendered either 0 hooks or 11 depending on whether the route
  // carried state. The guard now lives after the hook block.
  const [digits,    setDigits]    = useState(['', '', '', '', '', '']);
  const [pwd,       setPwd]       = useState('');
  const [confirmPwd,setConfirmPwd] = useState('');
  // Read-only: this page has no resend flow, so the dev code never changes
  // after the route hands it over.
  const [devOtp]                  = useState(initialDev);
  const [submit,    setSubmit]    = useState(false);
  const [serverErr, setServerErr] = useState(null);
  const [okMsg,     setOkMsg]     = useState(null);
  const [copied,    setCopied]    = useState(false);

  const inputRefs = useRef([]);
  useEffect(() => {
    if (!identifier) return;
    inputRefs.current[0]?.focus();
  }, [identifier]);

  const code = useMemo(() => digits.join(''), [digits]);

  if (!identifier) return <Navigate to="/forgot-password" replace />;

  function setDigit(idx, value) {
    const v = value.replace(/\D/g, '').slice(0, 1);
    setDigits(d => {
      const next = d.slice();
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (serverErr) setServerErr(null);
  }

  function onKeyDown(idx, ev) {
    if (ev.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (ev.key === 'ArrowLeft' && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (ev.key === 'ArrowRight' && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  }

  function onPaste(ev) {
    const text = (ev.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    ev.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
  }

  function fillFromDevOtp(value) {
    const text = String(value || '').replace(/\D/g, '').slice(0, 6);
    if (text.length !== 6) return;
    setDigits(text.split(''));
    inputRefs.current[5]?.focus();
  }

  async function copyDevOtp() {
    try {
      await navigator.clipboard.writeText(String(devOtp || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  }

  function validate() {
    if (code.length !== 6) {
      setServerErr('Enter all 6 digits.');
      return false;
    }
    if (pwd.length < 10) {
      setServerErr('Password must be at least 10 characters.');
      return false;
    }
    if (!/[a-z]/.test(pwd) || !/[A-Z]/.test(pwd) || !/\d/.test(pwd)) {
      setServerErr('Password must include upper, lower, and a number.');
      return false;
    }
    if (pwd !== confirmPwd) {
      setServerErr('Passwords do not match.');
      return false;
    }
    return true;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    setServerErr(null);
    setOkMsg(null);
    if (!validate()) return;
    setSubmit(true);
    try {
      const res = await authApi.resetPassword(identifier, code, pwd);
      setOkMsg(res?.message || 'Password reset. Please sign in with your new password.');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};
      if (status === 422) {
        setServerErr(data.message || 'Invalid or expired reset code.');
      } else if (status === 429) {
        setServerErr(data.message || 'Too many attempts. Please try again later.');
      } else if (!err?.response) {
        setServerErr('Could not reach the NACIS API. Check your connection.');
      } else {
        setServerErr(data.message || 'Reset failed. Try again.');
      }
    } finally {
      setSubmit(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="relative hidden bg-slate-950 lg:flex lg:flex-col lg:p-12 lg:text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(900px circle at 0% 0%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(800px circle at 100% 100%, rgba(14,165,233,0.18), transparent 45%)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-1 flex-col">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10">
              <Shield className="h-6 w-6" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-wide">NACIS</div>
              <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">National AI Communications-Intelligence System</div>
            </div>
          </Link>
        </div>
      </aside>

      <main className="relative flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="absolute right-4 top-4 lg:right-8 lg:top-8">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Set a new password</h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Enter the 6-digit code we sent to <span className="font-medium text-slate-800">{identifier}</span> and your new password.
          </p>

          {devOtp && (
            <div className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Wrench className="h-4 w-4" />
                Dev mode — email not sent
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 rounded bg-white/60 px-3 py-2">
                <button
                  type="button"
                  onClick={copyDevOtp}
                  className="font-mono text-2xl font-bold tracking-[0.4em] text-amber-900 hover:text-amber-700"
                  title="Click to copy"
                >
                  {devOtp}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={copyDevOtp}
                    className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    {copied ? <ClipboardCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={() => fillFromDevOtp(devOtp)}
                    className="inline-flex items-center rounded bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-500"
                  >
                    Use this code
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs text-amber-800">
                Stage and production environments still email this code normally.
              </p>
            </div>
          )}

          {serverErr && (
            <div role="alert" className="mt-6 flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{serverErr}</div>
            </div>
          )}

          {okMsg && (
            <div role="status" className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              {okMsg}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
            <div>
              <label className="block text-sm font-medium text-slate-800">Reset code</label>
              <div className="mt-2 flex justify-between gap-2" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => setDigit(i, e.target.value)}
                    onKeyDown={e => onKeyDown(i, e)}
                    className="h-14 w-12 rounded-md border border-slate-300 bg-white text-center text-xl font-semibold text-slate-900 shadow-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
                    disabled={submit}
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="pwd" className="block text-sm font-medium text-slate-800">New password</label>
              <input
                id="pwd"
                type="password"
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                className="input mt-1"
                autoComplete="new-password"
                disabled={submit}
              />
              <div className="mt-1 text-xs text-slate-500">Min 10 chars, upper + lower + number.</div>
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-slate-800">Confirm new password</label>
              <input
                id="confirm"
                type="password"
                value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
                className="input mt-1"
                autoComplete="new-password"
                disabled={submit}
              />
            </div>

            <button type="submit" className="btn-primary w-full" disabled={submit}>
              {submit ? <><Loader2 className="h-4 w-4 animate-spin" /> Resetting…</> : 'Reset password'}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500">
            <Link to="/login" className="font-medium text-sky-700 hover:text-sky-800">
              Back to sign-in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
